import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import { PrimaryButton, Stack } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import React, { useState } from 'react';

import { TrackingEntityDocumentData } from '../__generated__/TrackingEntityDocumentData';
import {
  POEntityDocumentRetireBalance,
  POEntityDocumentRetireBalanceVariables,
} from './__generated__/POEntityDocumentRetireBalance';
import {
  POEntityDocumentUnretireBalance,
  POEntityDocumentUnretireBalanceVariables,
} from './__generated__/POEntityDocumentUnretireBalance';
import RETIRE_BALANCE from './POEntityDocumentRetireBalance.graphql';
import UNRETIRE_BALANCE from './POEntityDocumentUnretireBalance.graphql';
const TITLE_RETIRE = 'Are you sure you want to retire the remaining balance?';
const TITLE_UNRETIRE = 'Are you sure you want to remove the retire amount?';

export const RetireBalance: React.FC<{
  documentData: TrackingEntityDocumentData | undefined;
  onMutationSuccess: () => void;
}> = ({ documentData, onMutationSuccess }) => {
  const { _unretirePrivilege, _retirePrivilege } = {
    ...documentData?.entityDocument,
  };
  const [dialogState, setDialogState] = useState<{
    visible: boolean;
    retire: boolean;
  }>({ visible: false, retire: false });

  const [retire] = useMutation<
    POEntityDocumentRetireBalance,
    POEntityDocumentRetireBalanceVariables
  >(RETIRE_BALANCE, { errorPolicy: 'all' });

  const [unRetire] = useMutation<
    POEntityDocumentUnretireBalance,
    POEntityDocumentUnretireBalanceVariables
  >(UNRETIRE_BALANCE, { errorPolicy: 'all' });

  const onConfirm = async () => {
    const { _rowTimestamp, id } = { ...documentData?.entityDocument };

    if (!dialogState.retire) {
      const { errors } = await unRetire({
        variables: {
          input: {
            rowTimestamp: _rowTimestamp!,
            entityDocumentId: id!,
          },
        },
      });

      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        setDialogState({ retire: dialogState.retire, visible: false });
        onMutationSuccess();
        showCustomToast({
          content: 'Balance Unretired successfully',
          appearance: 'success',
        });
      }
    } else {
      const { errors } = await retire({
        variables: {
          input: {
            rowTimestamp: _rowTimestamp!,
            entityDocumentId: id!,
          },
        },
      });

      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'Balance retired',
          appearance: 'success',
        });
        onMutationSuccess();
        setDialogState({ retire: dialogState.retire, visible: false });
      }
    }
  };

  return (
    <Stack>
      {_retirePrivilege && (
        <PrimaryButton
          text="Retire Balance"
          onClick={() => {
            setDialogState({ retire: true, visible: true });
          }}
        />
      )}
      {_unretirePrivilege && (
        <PrimaryButton
          text="Unretire Balance"
          onClick={() => {
            setDialogState({ retire: false, visible: true });
          }}
        />
      )}

      <ConfirmDialog
        hidden={!dialogState.visible}
        title={dialogState.retire ? TITLE_RETIRE : TITLE_UNRETIRE}
        onDismiss={() => {
          setDialogState({ retire: dialogState.retire, visible: false });
        }}
        maxWidth={350}
        onConfirm={async () => {
          onConfirm();
        }}
      />
    </Stack>
  );
};
